import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCurrency, formatDateForDisplay } from '@/lib/utils';
import { router } from '@inertiajs/react';
import { Building2, CalendarClock, UserRound } from 'lucide-react';
import type { CalculationRequest, CalculationRequestOptions } from '../types';
import { OutcomeBadge, OverdueBadge, PhaseBadge, StatusBadge, userName } from './calculation-request-badges';

interface CalculationRequestCardProps {
    request: CalculationRequest;
    /** Only the two badge catalogues the card prints — so callers holding a subset can render it too. */
    options: Pick<CalculationRequestOptions, 'statuses' | 'phases'>;
    canViewFinancials?: boolean;
}

/** Card view used on mobile and in the toolbar's grid mode. */
export function CalculationRequestCard({ request, options, canViewFinancials = false }: CalculationRequestCardProps) {
    const { t } = useTranslations();
    const dash = t('calculation_requests.tables.none', '—');

    return (
        <Card className="hover:bg-accent/40 cursor-pointer transition-colors" onClick={() => router.visit(`/calculation-requests/${request.id}`)}>
            <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="font-medium">{request.request_number ?? dash}</span>
                            {request.is_overdue ? <OverdueBadge /> : null}
                        </div>
                        <p className="text-muted-foreground mt-0.5 truncate text-sm">{request.object_name}</p>
                    </div>
                    <StatusBadge value={request.status} options={options.statuses} />
                </div>

                <div className="text-muted-foreground grid gap-1.5 text-sm">
                    <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{request.company?.name ?? dash}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <UserRound className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                            {request.assignee ? userName(request.assignee) : t('calculation_requests.tables.unassigned', 'Unassigned')}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                        <span>{request.requested_calculation_date ? formatDateForDisplay(request.requested_calculation_date) : dash}</span>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                    <div className="flex items-center gap-2">
                        <PhaseBadge value={request.project_phase} options={options.phases} />
                        <OutcomeBadge value={request.outcome} />
                    </div>
                    {canViewFinancials && request.calculated_amount !== null && request.calculated_amount !== undefined ? (
                        <span className="text-sm font-medium">{formatCurrency(Number(request.calculated_amount))}</span>
                    ) : null}
                </div>
            </CardContent>
        </Card>
    );
}
